import { useEffect, useRef } from 'react';
import { api } from './api';

/**
 * Cart auto-logout after 5 idle minutes (§3, §0.5, §0.6) — and NEVER mid-call.
 *
 * Two halves that must agree:
 *   · the SERVER refuses a cart session idle for 5 minutes, unless that member
 *     has a live call (api/lib/session.ts). That is the real rule; it holds even
 *     if this browser misbehaves or is killed.
 *   · this hook is the courtesy half: it notices the same 5 minutes locally and
 *     returns to the name-pick screen promptly, rather than leaving the last
 *     person's name on screen until someone touches it.
 *
 * "Idle" means no HUMAN interaction. Background polling deliberately does not
 * count — otherwise a cart left alone would stay signed in forever.
 */

const IDLE_MS = 5 * 60 * 1000;
/** Don't ping the server on every touch — once a minute of activity is plenty. */
const PING_THROTTLE_MS = 60 * 1000;

const ACTIVITY_EVENTS = ['pointerdown', 'keydown', 'touchstart', 'wheel'] as const;

export function useIdleLogout(opts: {
  enabled: boolean;
  /** While true the timer is suspended: a call must never be cut off (§0.5). */
  paused: boolean;
  onIdle: () => void;
}) {
  const { enabled, paused, onIdle } = opts;
  const onIdleRef = useRef(onIdle);
  onIdleRef.current = onIdle;

  const lastPingRef = useRef(0);

  useEffect(() => {
    if (!enabled || paused) return;

    let timer: ReturnType<typeof setTimeout>;

    const reset = () => {
      clearTimeout(timer);
      timer = setTimeout(() => onIdleRef.current(), IDLE_MS);
    };

    const onActivity = () => {
      reset();
      // Tell the server this was a human, so its own idle clock advances too.
      const now = Date.now();
      if (now - lastPingRef.current > PING_THROTTLE_MS) {
        lastPingRef.current = now;
        void api('/api/auth/me', { userActive: true }).catch(() => {
          // A failed keep-alive is not worth interrupting anyone for; the next
          // interaction retries, and the server remains the authority.
        });
      }
    };

    for (const ev of ACTIVITY_EVENTS) {
      window.addEventListener(ev, onActivity, { passive: true });
    }
    reset();

    return () => {
      clearTimeout(timer);
      for (const ev of ACTIVITY_EVENTS) window.removeEventListener(ev, onActivity);
    };
  }, [enabled, paused]);
}
